import React from 'react'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { useStyles } from './style'

const toText = (v) => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

const TooltipWrap = React.memo(function TooltipWrap({ title, children }) {
  const classes = useStyles()
  return (
    <Tooltip title={toText(title)} arrow placement="top">
      <span className={classes.tooltipSpan}>{children}</span>
    </Tooltip>
  )
})

const Header = React.memo(function Header({ title }) {
  const classes = useStyles()

  return (
    <div className={classes.headerOuter}>
      <div className={classes.headerInner}>
        <div className={classes.headerBar} />
        <TooltipWrap title={title}>
          <Typography className={classes.headerTitle}>{`${title} : `}</Typography>
        </TooltipWrap>
      </div>
    </div>
  )
})

const CommonLinkedInBuiltTable = React.memo(function CommonLinkedInBuiltTable(props) {
  const { title, data } = props || {}
  const classes = useStyles()

  const rows = React.useMemo(() => (Array.isArray(data) ? data : []), [data])

  const columns = React.useMemo(() => {
    if (!rows.length) return []
    const map = new Map()

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!row || typeof row !== 'object') continue

      const keys = Object.keys(row)
      for (let k = 0; k < keys.length; k++) {
        const key = keys[k]
        if (map.has(key)) continue
        const cell = row[key]
        map.set(key, cell?.label ?? key)
      }
    }

    return Array.from(map, ([key, label]) => ({ key, label }))
  }, [rows])

  const getRowKey = React.useCallback((row, idx) => row?.id ?? row?._id ?? idx, [])

  if (!rows.length) {
    return (
      <div className={classes.wrapper2}>
        {title ? <Header title={title} /> : null}
        <Paper className={classes.tableOuter} elevation={0}>
          <div className={classes.emptyState}>No data available</div>
        </Paper>
      </div>
    )
  }

  return (
    <div className={classes.wrapper2}>
      {title ? <Header title={title} /> : null}

      <Paper className={classes.tableOuter} elevation={0}>
        <div className={classes.scrollWrap}>
          <TableContainer>
            <Table stickyHeader className={classes.table} size="small" aria-label="dynamic table">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.key} className={classes.headCell}>
                      <TooltipWrap title={col.label}>
                        <span className={classes.ellipsis}>{toText(col.label)}</span>
                      </TooltipWrap>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, rowIndex) => {
                  const rowKey = getRowKey(row, rowIndex)

                  return (
                    <TableRow key={rowKey} className={classes.rowHover}>
                      {columns.map((col) => {
                        const cellObj = row?.[col.key]
                        const cellValue = cellObj?.value ?? ''
                        const cellLabel = cellObj?.label ?? col.label ?? col.key
                        const tooltipText = `${toText(cellLabel)}: ${toText(cellValue)}`

                        return (
                          <TableCell key={`${rowKey}-${col.key}`} className={classes.bodyCell}>
                            <TooltipWrap title={tooltipText}>
                              <span className={classes.ellipsis}>{toText(cellValue)}</span>
                            </TooltipWrap>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Paper>
    </div>
  )
})

export default CommonLinkedInBuiltTable
